import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { PageTabRail } from '../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../../components/router/router-tabbed-navigation';
import { useDocumentPageContext } from '../../../../../../../../../../features/document/document-page-context';
import { DocumentsTable, type DocumentsTableHeaderProps } from '../../../../../../../../../../features/document/table/documents-table';
import { AccountYearSelect } from '../../../../../../../../../../features/registration/account/account-year-select/account-year-select';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';
import type { ViewContext } from '../../../../../../../../../../util/view-context';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/document/_document/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountDocuments,
      label: 'Statements',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountDocuments>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-documents-${params.accountId}`,
        label: 'Documents',
        to: `/registration/account/${params.accountId}/document`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Account Statements'),
  component: AccountStatementDocumentsList,
});

function TableHeader(_props: DocumentsTableHeaderProps) {
  const { accountId } = Route.useParams();
  const { year, setYear } = useDocumentPageContext();

  return (
    <PageTabRail
      actions={
        <AccountYearSelect
          accountId={accountId}
          label="Document Year"
          name="account-statement-year-select"
          onChange={(e) => {
            const newYear = parseInt(e.target.value, 10);
            if (!Number.isNaN(newYear)) {
              setYear(newYear);
            }
          }}
          value={year.toString()}
        />
      }
      group={TabbedNavigationGroup.RegistrationAccountDocuments}
    />
  );
}

function AccountStatementDocumentsList() {
  const { accountId } = Route.useParams();
  const { year } = useDocumentPageContext();
  const viewContext: ViewContext = useMemo(
    () => ({
      kind: 'account',
      accountId,
    }),
    [accountId],
  );

  return (
    <DocumentsTable
      documentType="accountStatement"
      HeaderActions={TableHeader}
      renderWhenEmpty="No Account Statements"
      view={viewContext}
      year={year}
    />
  );
}
